import React from "react";

/**
 * Alert component for displaying various types of notifications
 *
 * @component
 * @param {Object} props - Component props
 * @param {string} props.message - The message to display in the alert
 * @param {('warning'|'strongWarning'|'error'|'info'|'success')} [props.type='warning'] - The type of alert which determines styling
 * @returns {JSX.Element} - Styled alert component with appropriate icon and message
 *
 * @example
 * // Warning alert (default)
 * <Alert message="This is a warning message" />
 *
 * @example
 * // Error alert
 * <Alert message="An error occurred" type="error" />
 *
 * @example
 * // Success alert
 * <Alert message="Operation completed successfully" type="success" />
 *
 * @example
 * // Info alert
 * <Alert message="Here's some information" type="info" />
 */
export default function Alert({ message, type = "warning" }) {
  // Define styles based on alert type
  const styles = {
    warning: {
      container: "bg-yellow-50 border-l-4 border-yellow-400 p-4",
      icon: "text-yellow-400",
      text: "text-yellow-700",
    },
    strongWarning: {
      container: "bg-red-50 border-l-4 border-red-400 p-4",
      icon: "text-red-400",
      text: "text-red-700",
    },
    error: {
      container: "bg-red-50 border-l-4 border-red-400 p-4",
      icon: "text-red-400",
      text: "text-red-700",
    },
    info: {
      container: "bg-blue-50 border-l-4 border-blue-400 p-4",
      icon: "text-blue-400",
      text: "text-blue-700",
    },
    success: {
      container: "bg-green-50 border-l-4 border-green-400 p-4",
      icon: "text-green-400",
      text: "text-green-700",
    },
  };

  const currentStyle = styles[type] || styles.warning;

  return (
    <div className={currentStyle.container}>
      <div className="flex">
        <div className="flex-shrink-0">
          <svg
            className={`h-5 w-5 ${currentStyle.icon}`}
            xmlns="http://www.w3.org/2000/svg"
            viewBox="0 0 20 20"
            fill="currentColor"
            aria-hidden="true"
          >
            {type === "success" ? (
              <path
                fillRule="evenodd"
                d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z"
                clipRule="evenodd"
              />
            ) : type === "error" ? (
              <path
                fillRule="evenodd"
                d="M10 18a8 8 0 100-16 8 8 0 000 16zM8.707 7.293a1 1 0 00-1.414 1.414L8.586 10l-1.293 1.293a1 1 0 101.414 1.414L10 11.414l1.293 1.293a1 1 0 001.414-1.414L11.414 10l1.293-1.293a1 1 0 00-1.414-1.414L10 8.586 8.707 7.293z"
                clipRule="evenodd"
              />
            ) : type === "info" ? (
              <path
                fillRule="evenodd"
                d="M18 10a8 8 0 11-16 0 8 8 0 0116 0zm-7-4a1 1 0 11-2 0 1 1 0 012 0zM9 9a1 1 0 000 2h2a1 1 0 100-2H9z"
                clipRule="evenodd"
              />
            ) : (
              <path
                fillRule="evenodd"
                d="M8.257 3.099c.765-1.36 2.722-1.36 3.486 0l5.58 9.92c.75 1.334-.213 2.98-1.742 2.98H4.42c-1.53 0-2.493-1.646-1.743-2.98l5.58-9.92zM11 13a1 1 0 11-2 0 1 1 0 012 0zm-1-8a1 1 0 00-1 1v3a1 1 0 002 0V6a1 1 0 00-1-1z"
                clipRule="evenodd"
              />
            )}
          </svg>
        </div>
        <div className="ml-3">
          <p className={`text-sm ${currentStyle.text}`}>{message}</p>
        </div>
      </div>
    </div>
  );
}
